import { useCallback, useEffect, useRef, useState } from 'react';

import { registerNavigationLeaveGate } from '@/core/lib/navigation-leave-gate';

type LeaveRequest = {
  proceed: () => void;
};

/**
 * Blocks shell navigation / browser leave while `enabled` and surfaces a leave
 * confirmation dialog via `isLeavePromptOpen` / `confirmLeave` / `cancelLeave`.
 *
 * Ionic's `IonReactRouter` wraps React Router v6 `BrowserRouter` (not a data
 * router), so RR6 `useBlocker` is unavailable. Instead:
 * - `navigateShell` / `ShellLink` / sidebar go through `navigation-leave-gate`
 * - mobile `IonTabButton` clicks consult the same gate (preventDefault + proceed)
 * - `beforeunload` covers tab/window close; `popstate` covers browser back
 */
export function useNavigationLeaveGuard(enabled: boolean) {
  const [leaveRequest, setLeaveRequest] = useState<LeaveRequest | null>(null);
  const enabledRef = useRef(enabled);
  const bypassRef = useRef(false);
  const promptOpenRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    promptOpenRef.current = leaveRequest != null;
  }, [leaveRequest]);

  useEffect(() => {
    return registerNavigationLeaveGate({
      shouldBlock: () => enabledRef.current && !bypassRef.current,
      onAttempt: (proceed) => {
        if (promptOpenRef.current) return;
        setLeaveRequest({
          proceed: () => {
            bypassRef.current = true;
            setLeaveRequest(null);
            proceed();
            window.setTimeout(() => {
              bypassRef.current = false;
            }, 0);
          },
        });
      },
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (bypassRef.current || !enabledRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    const onPopState = () => {
      if (bypassRef.current || !enabledRef.current || promptOpenRef.current) {
        return;
      }

      window.history.pushState(null, '', window.location.href);

      setLeaveRequest({
        proceed: () => {
          bypassRef.current = true;
          setLeaveRequest(null);
          window.history.back();
          window.setTimeout(() => {
            bypassRef.current = false;
          }, 0);
        },
      });
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPopState);
    };
  }, [enabled]);

  const confirmLeave = useCallback(() => {
    leaveRequest?.proceed();
    setLeaveRequest(null);
  }, [leaveRequest]);

  const cancelLeave = useCallback(() => {
    setLeaveRequest(null);
  }, []);

  const runWithoutGuard = useCallback((action: () => void) => {
    bypassRef.current = true;
    try {
      action();
    } finally {
      window.setTimeout(() => {
        bypassRef.current = false;
      }, 0);
    }
  }, []);

  return {
    isLeavePromptOpen: leaveRequest != null,
    confirmLeave,
    cancelLeave,
    runWithoutGuard,
    setLeavePromptOpen: (open: boolean) => {
      if (!open) cancelLeave();
    },
  };
}
